"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { DEFAULT_PANEL_SECTION, isConnectPanelSection, type ConnectPanelSection } from "@/lib/connect/panel-sections";

// Lazy: the panel pulls the snippet builders and pickers, which must not ride
// every route's bundle for users who never open it (same trick as the
// dashboard assistant pane).
const ConnectPanelSheet = dynamic(() => import("@/components/connect-panel/connect-panel").then(module => module.ConnectPanel), {
  ssr: false
});

/** A key minted inside the panel this page-load; the raw value exists nowhere else. */
export interface MintedPanelKey {
  apiKey: string;
  name: string;
  keyPrefix: string;
}
interface ConnectPanelContextValue {
  isOpen: boolean;
  /** Sticky: true once the panel has ever been opened this mount — gates the lazy sheet. */
  hasOpened: boolean;
  /** The section the panel should be (scrolled) at; set via open(section). */
  activeSection: ConnectPanelSection;
  open: (section?: ConnectPanelSection) => void;
  setOpen: (open: boolean) => void;
  /**
   * Held in the provider (not the sheet subtree) so an accidental Esc/overlay
   * close doesn't lose a once-shown key mid-setup. Memory only — gone on
   * refresh, never persisted.
   */
  mintedKey: MintedPanelKey | null;
  setMintedKey: (key: MintedPanelKey | null) => void;
}
const ConnectPanelContext = createContext<ConnectPanelContextValue | null>(null);
export function useConnectPanel() {
  const context = useContext(ConnectPanelContext);
  if (!context) {
    throw new Error("useConnectPanel must be used within ConnectPanelProvider");
  }
  return context;
}

/**
 * Same context, tolerating absence. Needed by PrimaryNav: logged-out /chat
 * renders ChatNav (and with it PrimaryNav) outside every shell provider —
 * the same reason ChatNav null-guards useCommandPalette.
 */
export function useOptionalConnectPanel() {
  return useContext(ConnectPanelContext);
}

/**
 * Open/close state for the Connect panel (AIR-707), the Supabase-style
 * slide-over rebuilt as one scrolling Quick start page. Mounted once in
 * MainShell so it's reachable from any dashboard route; deep-linkable via
 * `?connect=<section>`.
 */
export function ConnectPanelProvider(t0) {
  const $ = _c(15);
  const {
    children
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [activeSection, setActiveSection] = useState(DEFAULT_PANEL_SECTION);
  const [mintedKey, setMintedKey] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = next => {
      setIsOpen(next);
      if (next) {
        setHasOpened(true);
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const setOpen = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = section => {
      setActiveSection(section ?? DEFAULT_PANEL_SECTION);
      setOpen(true);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const open = t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const requested = new URLSearchParams(window.location.search).get("connect");
      if (isConnectPanelSection(requested)) {
        open(requested);
      }
    };
    t4 = [open];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== activeSection || $[5] !== hasOpened || $[6] !== isOpen || $[7] !== mintedKey) {
    t5 = {
      isOpen,
      hasOpened,
      activeSection,
      open,
      setOpen,
      mintedKey,
      setMintedKey
    };
    $[4] = activeSection;
    $[5] = hasOpened;
    $[6] = isOpen;
    $[7] = mintedKey;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const value = t5;
  let t6;
  if ($[9] !== hasOpened) {
    t6 = hasOpened ? <ConnectPanelSheet /> : null;
    $[9] = hasOpened;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== children || $[12] !== t6 || $[13] !== value) {
    t7 = <ConnectPanelContext.Provider value={value}>{children}{t6}</ConnectPanelContext.Provider>;
    $[11] = children;
    $[12] = t6;
    $[13] = value;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}